// eslint-disable-next-line import/extensions
import SHA256 from 'crypto-js/sha256.js';

import api from '@/api/index';

import { getValidFbcValue } from './fbcValidator';
import { getCookieValue, randomString } from './utils';

// 购买事件允许的参数白名单
const PURCHASE_KEYS = ['value', 'currency'];

// 需要附加用户信息的事件类型
// const EVENTS_WITH_USER_DATA = [
//   'PageView',
//   'ViewContent',
//   'CompleteRegistration',
//   'AddToCart',
//   'AddToWishlist',
//   'InitiateCheckout',
//   'Lead',
//   'Purchase',
// ];

// 支持的货币类型
const SUPPORTED_CURRENCIES = ['USD', 'AUD', 'GBP', 'CAD', 'EUR'];
const sha256 = (value) => SHA256(value).toString();

/**
 * 从单个name字段中提取firstname和lastname
 * @param {string} name - 完整的名字
 * @returns {Object} - 返回包含firstname和lastname的对象
 */
const splitName = (name) => {
  if (!name || typeof name !== 'string') {
    return { firstname: '', lastname: '' };
  }

  const trimmedName = name.trim();
  const spaceIndex = trimmedName.indexOf(' ');

  if (spaceIndex === -1) {
    // 没有空格，整个名字作为firstname
    return {
      firstname: trimmedName,
      lastname: trimmedName,
    };
  }

  // 第一个空格前的部分作为firstname，剩余部分作为lastname
  return {
    firstname: trimmedName.substring(0, spaceIndex).trim(),
    lastname: trimmedName.substring(spaceIndex + 1).trim(),
  };
};

/**
 * 从localStorage获取用户信息
 */
const getUserInfo = () => {
  try {
    const userInfoStr = localStorage.getItem('voo-userInfo');
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : {};

    // 如果没有firstname/lastname但有name字段，则拆分name
    if (userInfo.name && (!userInfo.firstname || !userInfo.lastname)) {
      const { firstname, lastname } = splitName(userInfo.name);
      userInfo.firstname = userInfo.firstname || firstname;
      userInfo.lastname = userInfo.lastname || lastname;
    }

    return userInfo;
  } catch (error) {
    console.error(error);
    return {};
  }
};

/**
 * 计算加密后的电话号码
 */
const getHashedPhone = (userInfor, needSha256 = true) => {
  if (!userInfor.telephone) {
    return null;
  }
  const phone = userInfor.tel_code
    ? `${userInfor.tel_code}${userInfor.telephone}`
    : userInfor.telephone;
  return needSha256 ? sha256(phone.replace(/[^0-9]/g, '')) : phone.replace(/[^0-9]/g, '');
};

/**
 * 转换生日格式从 MM/DD/YYYY 到 YYYYMMDD
 * @param {string} birthday - 原始生日格式，如 "10/07/2015"
 * @returns {string|null} - 返回格式化后的生日 "20151007" 或 null
 */
const formatBirthday = (birthday) => {
  if (!birthday || typeof birthday !== 'string') {
    return null;
  }

  try {
    // 处理 MM/DD/YYYY 格式
    const parts = birthday.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // 补零确保格式正确（兼容 IE11）
      const formattedMonth = month.length === 1 ? `0${month}` : month;
      const formattedDay = day.length === 1 ? `0${day}` : day;
      return `${year}${formattedMonth}${formattedDay}`;
    }
    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * 生成加密后的用户数据
 */
const getHashedUserData = (userInfor) => {
  try {
    const hashedData = {
      em: userInfor.email ? sha256(userInfor.email.toLowerCase()) : null,
      ph: getHashedPhone(userInfor),
      fn: userInfor.firstname ? sha256(userInfor.firstname.toLowerCase()) : null,
      ln: userInfor.lastname ? sha256(userInfor.lastname.toLowerCase()) : null,
      country: userInfor.country_id ? sha256(userInfor.country_id.toLowerCase()) : null,
      external_id: userInfor.id ? sha256(String(userInfor.id)) : null,
    };

    // 添加性别（加密）
    if (userInfor.gender) {
      const gender = userInfor.gender === 1 ? 'm' : userInfor.gender === 2 ? 'f' : null;
      hashedData.ge = gender ? sha256(gender) : null;
    }

    // 添加生日（加密）
    if (userInfor.birthday) {
      const formattedBirthday = formatBirthday(userInfor.birthday);
      hashedData.db = formattedBirthday ? sha256(formattedBirthday) : null;
    }

    return hashedData;
  } catch (error) {
    console.error(error);
    return {};
  }
};

/**
 * 为自定义数据添加用户信息
 */
const enrichCustomDataWithUserInfo = (custom, userInfor) => {
  try {
    if (userInfor.email) {
      custom.customer_email = userInfor.email.toLowerCase();
      custom.em = userInfor.email.toLowerCase();
    }
    if (userInfor.id) {
      custom.customer_id = userInfor.id;
      custom.external_id = String(userInfor.id);
    }
    if (userInfor.created_at) {
      custom.created_at = userInfor.created_at;
    }
    if (userInfor.first_order_at) {
      custom.min = userInfor.first_order_at;
    }
    if (userInfor.firstname) {
      custom.fn = userInfor.firstname.toLowerCase();
    }
    if (userInfor.lastname) {
      custom.ln = userInfor.lastname.toLowerCase();
    }
    if (userInfor.country_id) {
      custom.country = userInfor.country_id.toLowerCase();
    }
    if (userInfor.gender) {
      custom.ge = userInfor.gender === 1 ? 'm' : userInfor.gender === 2 ? 'f' : null;
    }
    if (userInfor.birthday) {
      custom.db = formatBirthday(userInfor.birthday);
    }
    custom.ph = getHashedPhone(userInfor, false);
    custom.action_source = 'website';
  } catch (error) {
    console.error(error);
  }
};

/**
 * 验证购买事件数据
 */
const validatePurchaseData = (custom) => {
  try {
    const customKeys = Object.keys(custom);
    // 检查是否有缺失的必需参数
    const hasMissingKeys = PURCHASE_KEYS.some((key) => !customKeys.includes(key));
    if (hasMissingKeys) return false;
    // 要求购买事件的value不能大于999
    if (custom.value > 999) return false;
    // 要求购买事件的currency必须是支持的货币类型
    if (!SUPPORTED_CURRENCIES.includes(custom.currency)) return false;
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

/**
 * 判断是否应该发送事件
 * 1. sgtmPixelConfig.facebook = false 时完全禁用客户端上报（优先判断）
 * 2. 非生产环境且无调试开关时不上报
 */
const shouldSendEvent = () => {
  if (window.sgtmPixelConfig?.facebook === false) return false;
  const runtimeConfig = useRuntimeConfig();
  return (
    runtimeConfig.public.VUE_APP_MODE === 'production' ||
    sessionStorage.getItem('facebook-send-open')
  );
};

/**
 * 临时调试代码生成 traceId（RFC 4122 v4 形态），兼容无 crypto.randomUUID 的环境（如旧版 Safari / WebView）
 */
const getTraceid = () => {
  const c = typeof window !== 'undefined' && window.crypto ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return `0326trace-crypto-${c.randomUUID()}`;
  }
  if (c && typeof c.getRandomValues === 'function') {
    const buf = new Uint8Array(16);
    c.getRandomValues(buf);
    buf[6] = 64 + (buf[6] % 16);
    buf[8] = 128 + (buf[8] % 64);
    const h = (i) => buf[i].toString(16).padStart(2, '0');
    return `0326trace-random1-${h(0)}${h(1)}${h(2)}${h(3)}-${h(4)}${h(5)}-${h(6)}${h(7)}-${h(8)}${h(9)}-${h(10)}${h(11)}${h(12)}${h(13)}${h(14)}${h(15)}`;
  }
  return '0326trace-random2-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : 8 + (r % 4);
    return v.toString(16);
  });
};

/**
 * 解析 Facebook 事件 ID，未传入时统一复用 randomString 生成。
 * @param {Object} custom - Facebook 事件自定义参数
 * @returns {string} Facebook 事件 ID
 */
const resolveFacebookEventId = (custom = {}) => custom.event_id || randomString();

/**
 * 更新 Facebook 用户数据
 * 用于登录/注册后调用 fbq('set', userData) 更新用户信息
 */
const updateUserData = () => {
  const userInfor = getUserInfo();
  if (!userInfor || !userInfor.id) {
    console.warn('updateUserData: No user info available');
    return;
  }

  const hashedUserData = getHashedUserData(userInfor);
  // 过滤掉 null 值
  const filteredUserData = Object.fromEntries(
    Object.entries(hashedUserData).filter(([, v]) => v !== null),
  );

  if (Object.keys(filteredUserData).length > 0) {
    const { public: publicConfig } = useRuntimeConfig();
    window.fbq('init', publicConfig.VUE_APP_FACEBOOK_PIXEL_ID, filteredUserData);
    console.info('fbq userData updated', filteredUserData);
  }
};

const setFbq = function (eventName, custom = {}) {
  const eventId = resolveFacebookEventId(custom);
  const userInfor = getUserInfo();
  const traceid = getTraceid();

  // 确保 content_ids 为数组格式
  if (custom.content_ids && !Array.isArray(custom.content_ids)) {
    custom.content_ids = [custom.content_ids];
  }

  try {
    // 所有事件都添加用户信息
    enrichCustomDataWithUserInfo(custom, userInfor);
  } catch (error) {
    console.error(error);
  }

  /* 只有生产环境才上传事件，非生产环境上传事件没有意义，fb不会采纳 */
  if (!shouldSendEvent()) {
    console.warn('fbq event not sent-- 非生产环境上报，不发送事件');
    return;
  }

  // 传递购买事件如果不符合条件，直接return，排除非正常数据影响
  if (eventName === 'Purchase' && !validatePurchaseData(custom)) {
    console.error(
      'Purchase fbq event not sent-- 购买事件数据不符合条件，不发送事件',
      JSON.stringify(custom),
    );
    return;
  }
  console.info('fbq event sent-- 发送事件', eventName, JSON.stringify(custom));

  // 发送浏览器事件和调接口发送服务器事件
  window.fbq('track', eventName, custom, { eventID: eventId });
  // 注：后端会增加字段:
  // event_time
  // client_ip_address
  // client_user_agent
  // action_source
  const eventParams = {
    event_name: eventName,
    event_id: eventId,
    traceid,
    event_source_url: location.href,
    action_source: 'website',
    client_user_agent: navigator.userAgent,
    referrer_url: document.referrer || null,
    user_data: {
      email: userInfor.email || null,
      firstname: userInfor.firstname || null,
      lastname: userInfor.lastname || null,
      // 增加加密版用户信息
      ...getHashedUserData(userInfor),
      fbp: getCookieValue('_fbp') || null,
      fbc: getValidFbcValue() || null,
    },
    custom_data: custom,
  };

  try {
    api.others.fbApi(eventParams, {}, { traceid });
  } catch (error) {
    console.error('fbq event sent error-- 发送事件失败', error);
  }

  try {
    const endTime = 1774886400000; // 2026-03-31
    const now = Date.now();
    if (now < endTime) {
      api.common.collectError({ json_error: JSON.stringify(eventParams) }, {}, { traceid });
    }
  } catch (error) {
    console.error('fbq event collect error error-- 收集事件失败', error);
  }
};

export { getHashedUserData, setFbq, updateUserData };
export default setFbq;
